import { ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { HOME_RATE_ROTATE_DWELL_MS, HOME_RATE_ROTATE_ROLL_MS, HOME_RATE_ROTATE_SNAP_MS } from '../../model/types/home-revamp-types';

@Component({
  selector: 'rmt-rotating-rate',
  templateUrl: 'rmt-rotating-rate.html'
})
export class RmtRotatingRateComponent implements OnChanges, OnDestroy {
  @Input() values: string[] = [];
  index: number = 0;
  anim: number = 0;
  easeIn: boolean = false;
  noTransition: boolean = false;
  shimmerToken: number = 0;
  private appliedValues: string[] = null;
  private dwellTimer: number = null;
  private rollTimer: number = null;
  private snapTimer: number = null;
  private reducedMotionQuery: MediaQueryList = null;
  private destroyed: boolean = false;
  private readonly onReducedMotionChange = () => this.zone.run(() => this.restart());

  constructor(private zone: NgZone, private changeDetector: ChangeDetectorRef) {
    if (typeof window.matchMedia === 'function') {
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reducedMotionQuery && typeof this.reducedMotionQuery.addListener === 'function')
        this.reducedMotionQuery.addListener(this.onReducedMotionChange);
    }
  }

  get isSingle(): boolean {
    return !this.values || this.values.length <= 1;
  }

  get currentValue(): string {
    const values = this.values || [];
    return values[this.index] || values[0] || '';
  }

  get reducedMotion(): boolean {
    return !!this.reducedMotionQuery && this.reducedMotionQuery.matches;
  }

  ngOnChanges() {
    const next = (this.values || []).slice();
    if (this.appliedValues && this.appliedValues.length === next.length && this.appliedValues.every((value, i) => value === next[i]))
      return;
    this.appliedValues = next;
    this.restart();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.teardown();
    if (this.reducedMotionQuery && typeof this.reducedMotionQuery.removeListener === 'function')
      this.reducedMotionQuery.removeListener(this.onReducedMotionChange);
  }

  restart() {
    this.teardown();
    this.index = 0;
    this.anim = 0;
    this.noTransition = false;
    if (this.isSingle)
      return;
    if (this.reducedMotion) {
      this.scheduleReducedSwap();
      return;
    }
    this.shimmerToken += 1;
    this.scheduleRoll();
  }

  private teardown() {
    [this.dwellTimer, this.rollTimer, this.snapTimer].forEach((timer) => {
      if (timer !== null)
        window.clearTimeout(timer);
    });
    this.dwellTimer = null;
    this.rollTimer = null;
    this.snapTimer = null;
  }

  private schedule(callback: () => void, delay: number): number {
    return this.zone.runOutsideAngular(() => window.setTimeout(() => {
      callback();
      if (!this.destroyed)
        this.changeDetector.detectChanges();
    }, delay));
  }

  private scheduleRoll() {
    this.dwellTimer = this.schedule(() => this.rollOut(), HOME_RATE_ROTATE_DWELL_MS);
  }

  private rollOut() {
    this.noTransition = false;
    this.easeIn = true;
    this.anim = 1;
    this.rollTimer = this.schedule(() => this.rollIn(), HOME_RATE_ROTATE_ROLL_MS);
  }

  private rollIn() {
    this.index = (this.index + 1) % this.values.length;
    this.noTransition = true;
    this.anim = -1;
    this.snapTimer = this.schedule(() => {
      this.noTransition = false;
      this.easeIn = false;
      this.anim = 0;
      this.rollTimer = this.schedule(() => this.onSettled(), HOME_RATE_ROTATE_ROLL_MS);
    }, HOME_RATE_ROTATE_SNAP_MS);
  }

  private onSettled() {
    this.shimmerToken += 1;
    this.scheduleRoll();
  }

  private scheduleReducedSwap() {
    this.dwellTimer = this.schedule(() => {
      this.index = (this.index + 1) % this.values.length;
      this.scheduleReducedSwap();
    }, HOME_RATE_ROTATE_DWELL_MS);
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-rotating-rate/rmt-rotating-rate.ts